/**
 * "The Grid" — the keeper penalty table.
 *
 * Source: 2019-II-1, which restated and extended 2017-II-1 ("The Grid Rule").
 * In the source Google Doc this rule is an EMBEDDED IMAGE, not text, which is why it does
 * not appear in any text export of the rulebook. Transcribed here from that image.
 *
 * The mechanic (2017-II-1 §2): when you keep a player, you forfeit the pick in his Penalty
 * Round. That Penalty Round then becomes his Round Drafted for the following year. A player
 * therefore walks up the table until he reaches a round that is Not Eligible.
 *
 *   Round Drafted -> Penalty Round -> Max Keeper Length
 *
 * The "Max Keeper Length" column printed in the rulebook is fully DERIVABLE by iterating the
 * penalty map, so it is not hard-coded here — it is computed by `maxKeeperYears()` and the
 * test suite asserts the computed values match the published table exactly. If those ever
 * disagree, the Grid itself is internally inconsistent and the league needs to resolve it.
 */

import type { LeagueConfig } from './config.js';

/** A player who cannot be kept at all. */
export const NOT_ELIGIBLE = null;
export type PenaltyRound = number | typeof NOT_ELIGIBLE;

/**
 * Round Drafted -> Penalty Round. Transcribed from the 2019-II-1 image.
 * Rounds 1 and 2 are Not Eligible: there is no earlier pick to forfeit.
 */
export const GRID_ROUNDS = 19;

/**
 * The rulebook's own map, kept separately from the one in force.
 *
 * `5d` gives a commissioner a Grid editor with a **Reset to the rulebook** control beside it, and
 * that control needs something to reset TO. This is it: the transcription of the 2019-II-1 image,
 * frozen. Whatever the league edits, the printed rule is still here to come back to.
 */
export const RULEBOOK_GRID: Readonly<Record<number, PenaltyRound>> = {
  1: NOT_ELIGIBLE, 2: NOT_ELIGIBLE, 3: 1, 4: 2, 5: 2, 6: 3, 7: 4, 8: 5, 9: 6, 10: 6,
  11: 7, 12: 8, 13: 9, 14: 10, 15: 11, 16: 12, 17: 12, 18: 12, 19: 12,
};

const GRID: Readonly<Record<number, PenaltyRound>> = {
  1: NOT_ELIGIBLE,
  2: NOT_ELIGIBLE,
  3: 1,
  4: 2,
  5: 2,
  6: 3,
  7: 4,
  8: 5,
  9: 6,
  10: 6,
  11: 7,
  12: 8,
  13: 9,
  14: 10,
  15: 11,
  16: 12,
  17: 12,
  18: 12,
  19: 12,
};

/**
 * The floor rule. 2019-II-1 §2: "If the draft expands further, every round from Round 16 and
 * on would be a 12th round keeper the next year in order to ensure players can only be kept
 * for a maximum of 4 years (unless the sleeper provision applies)."
 */
const FLOOR_ROUND = 16;
const FLOOR_PENALTY = 12;

/**
 * The penalty round a GM forfeits to keep a player currently valued at `roundDrafted`.
 * Returns NOT_ELIGIBLE (null) if the player cannot be kept.
 */
export function penaltyRound(roundDrafted: number): PenaltyRound {
  if (!Number.isInteger(roundDrafted) || roundDrafted < 1) {
    throw new RangeError(`roundDrafted must be a positive integer, got ${roundDrafted}`);
  }
  if (roundDrafted >= FLOOR_ROUND) return FLOOR_PENALTY;
  const p = GRID[roundDrafted];
  return p === undefined ? FLOOR_PENALTY : p;
}

/** Whether a player at this round value can be kept at all. */
export function isKeepable(roundDrafted: number): boolean {
  return penaltyRound(roundDrafted) !== NOT_ELIGIBLE;
}

/**
 * The full escalation chain for a player first valued at `roundDrafted`, ignoring the sleeper
 * provision. Each entry is the round forfeited in that keeper year.
 *
 * Example: a 16th-round pick returns [12, 8, 5, 2] — four keeper years, then he is a 2nd-round
 * value and 2nd-round values are Not Eligible.
 */
export function escalationChain(roundDrafted: number): number[] {
  const chain: number[] = [];
  let current = roundDrafted;
  // The Grid strictly decreases, so this always terminates; the guard is belt-and-braces
  // against a future edit that accidentally introduces a cycle.
  for (let guard = 0; guard < 64; guard++) {
    const p = penaltyRound(current);
    if (p === NOT_ELIGIBLE) return chain;
    chain.push(p);
    current = p;
  }
  throw new Error(`escalationChain did not terminate from round ${roundDrafted} — Grid has a cycle`);
}

/**
 * Max Keeper Length: how many consecutive seasons a player first valued at `roundDrafted` can
 * be kept. This is the length of his escalation chain.
 */
export function maxKeeperYears(roundDrafted: number): number {
  return escalationChain(roundDrafted).length;
}

/**
 * The round value assigned to an undrafted free agent.
 *
 * 2022 "Keeper Value of Undrafted Free Agent Adjustment" §1: "UDFA's kept for the following
 * year will be considered to be drafted in the round after the final round of the draft."
 * With a 16-round draft that is Round 17, whose penalty is Round 12. This rendered
 * 2018-III-1 (a flat 14th-round penalty) obsolete.
 */
export function udfaRoundValue(config: LeagueConfig): number {
  return config.draftRounds + 1;
}

/**
 * The keeper round a farm-system player costs once he enters the NFL.
 *
 * 2013-III-1 §4: NFL draft round + 10 (a 1st-round NFL pick costs an 11th-round fantasy pick).
 * 2018-II-1 §1/§3 cap that result at the last round of the fantasy draft, because a 7th-round
 * NFL pick would otherwise map to Round 17 in a 16-round draft.
 * 2018-II-1 §2: a farm player who goes undrafted in the NFL but makes an opening-day 53-man
 * roster costs the same last-round pick — pass `nflRound = null` for that case.
 */
export function farmGraduationRound(nflRound: number | null, config: LeagueConfig): number {
  if (nflRound === null) return config.draftRounds;
  if (!Number.isInteger(nflRound) || nflRound < 1 || nflRound > 7) {
    throw new RangeError(`NFL draft round must be 1-7 or null (UDFA), got ${nflRound}`);
  }
  return Math.min(nflRound + config.farmGraduationPenaltyOffset, config.draftRounds);
}

/** The published Max Keeper Length column, used by the test suite to validate the derivation. */
export const PUBLISHED_MAX_KEEPER_LENGTH: Readonly<Record<number, number>> = {
  1: 0,
  2: 0,
  3: 1,
  4: 1,
  5: 1,
  6: 2,
  7: 2,
  8: 2,
  9: 3,
  10: 3,
  11: 3,
  12: 3,
  13: 4,
  14: 4,
  15: 4,
  16: 4,
  17: 4,
  18: 4,
  19: 4,
};
